import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { AdvancedCareTeamMember, AdvancedTrackDefinition, AdvancedTrackLead } from '../../api/types'
import { roleLabelFor } from '../careTeam/roleCategories'
import LeadPicker from './LeadPicker'
import { useLeadDirectory } from './useLeadDirectory'

type Props = {
  track: AdvancedTrackDefinition | null
  careTeam: AdvancedCareTeamMember[]
  onCancel: () => void
  onConfirm: (track: AdvancedTrackDefinition, lead: AdvancedTrackLead) => void
}

type FormProps = Omit<Props, 'track'> & { track: AdvancedTrackDefinition }

// Only opened when nobody on the care team holds the owner role; the patient picking at booking is the default (ADR-096).
const StartTrackForm = ({ track, careTeam, onCancel, onConfirm }: FormProps) => {
  const directory = useLeadDirectory(careTeam)
  const [choice, setChoice] = useState<'patient' | 'assign'>('patient')
  const [lead, setLead] = useState<AdvancedTrackLead>(null)
  const role = track.ownerRole ? roleLabelFor(track.ownerRole) : 'provider'

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Open intake for {track.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Nobody on this patient&apos;s care team is a {role} yet.
        </Typography>
        <RadioGroup value={choice} onChange={(event) => setChoice(event.target.value as 'patient' | 'assign')}>
          <FormControlLabel
            value="patient"
            control={<Radio size="small" />}
            label={`The patient chooses their ${role} when they book their intake call`}
          />
          <FormControlLabel value="assign" control={<Radio size="small" />} label="Assign someone now" />
        </RadioGroup>
        {choice === 'assign' && (
          <Box sx={{ pt: 1 }}>
            <LeadPicker
              options={directory.optionsFor(track)}
              value={lead}
              onChange={setLead}
              label={`${track.title} led by`}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            disabled={choice === 'assign' && !lead}
            onClick={() => onConfirm(track, choice === 'assign' ? lead : null)}
          >
            Open intake
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const StartTrackDialog = ({ track, ...rest }: Props) => (track ? <StartTrackForm track={track} {...rest} /> : null)

export default StartTrackDialog
